/* eslint-disable unicorn/no-null */
import { create } from 'zustand'

import { API_CONFIG } from '@/config/api'
import { ApiError } from '@/config/apiClient'
import { getAccessToken } from '@/helpers/cookies'
import {
  getBrazilLaunchParameters,
  getRomaniaLaunchParameters,
  getSpainLaunchParameters,
} from '@/helpers/launchParameters'

import { useAuthStore } from './authStore'
import { useBrandsStore } from './brandsStore'
import { MARKETING_CONSENT, useProfileStore } from './profileStore'

export interface ErrorData {
  status?: number
  code?: string
  message?: string
  action?: string
  detail?: string
}

interface AppState {
  isInitialized: boolean
  isLoading: boolean
  error: string | null
  errorData: ErrorData | null
  bootstrapRoute: string | null
}

interface AppActions {
  runBootstrap: () => Promise<void>
  setInitialized: (value: boolean) => void
  setLoading: (value: boolean) => void
  setError: (error: string | null) => void
  setErrorData: (errorData: ErrorData | null) => void
  reset: () => void
}

export const useAppStore = create<AppState & AppActions>((set, get) => ({
  isInitialized: false,
  isLoading: false,
  error: null,
  errorData: null,
  bootstrapRoute: null,

  runBootstrap: async () => {
    if (get().isInitialized || get().isLoading) return
    console.log('[bootstrap] start')
    set({ isLoading: true, error: null, bootstrapRoute: null })

    try {
      // Step 1: Campaign check — fires for all users, no auth required
      console.log('[bootstrap] step 1: fetching brands')
      const campaignState = await useBrandsStore.getState().fetchBrands()
      console.log('[bootstrap] campaignState:', campaignState)
      if (campaignState === 'error') {
        console.log('[bootstrap] brands fetch failed → /error')
        set({
          isInitialized: true,
          isLoading: false,
          bootstrapRoute: '/error',
          errorData: { action: 'fetchBrands' },
        })
        return
      }
      if (campaignState !== 'active' && campaignState !== 'unknown') {
        console.log('[bootstrap] campaign not active → /home')
        set({ isInitialized: true, isLoading: false, bootstrapRoute: '/home' })
        return
      }

      // Each country has an explicit auth flow below. All flows share the same
      // post-auth steps: fetchAvailableGamesForBrands → fetchProfile (opt-in) → /home.
      // France additionally checks gravity consent before routing.

      // Spain: users arrive from the AGORA loyalty app via URL params (credentials + token).
      // Those params are exchanged for an MNGS session token. 403 means the user is
      // not eligible for the campaign → /not-available.
      if (API_CONFIG.tenantCode === 'carrefourspain') {
        console.log('[bootstrap] spain flow')

        // Dev login sets a cookie token directly — skip AGORA flow if already present
        if (getAccessToken()) {
          console.log('[bootstrap] spain: existing token, skipping AGORA flow')
          await useAuthStore.getState().initialize()
        } else {
          const launchParameters = getSpainLaunchParameters()
          if (!launchParameters) {
            console.log('[bootstrap] spain: missing launch params → /error')
            set({
              isInitialized: true,
              isLoading: false,
              bootstrapRoute: '/error',
              errorData: {
                code: 'missing_params',
                detail: 'Missing URL params: credentials, token',
              },
            })
            return
          }

          try {
            console.log('[bootstrap] spain: authenticating')
            await useAuthStore
              .getState()
              .initializeSpain(
                launchParameters.credentials,
                launchParameters.token
              )
          } catch (error) {
            if (error instanceof ApiError && error.status === 403) {
              console.log('[bootstrap] spain: 403 → /not-available')
              set({
                isInitialized: true,
                isLoading: false,
                bootstrapRoute: '/not-available',
              })
            } else {
              const status =
                error instanceof ApiError ? error.status : undefined
              console.log('[bootstrap] spain: auth error → /error', status)
              set({
                isInitialized: true,
                isLoading: false,
                bootstrapRoute: '/error',
                errorData: { status, code: 'auth_failed' },
              })
            }
            return
          }
        }

        try {
          await useBrandsStore.getState().fetchAvailableGamesForBrands()
        } catch {
          console.log(
            '[bootstrap] spain: available games fetch failed (non-blocking)'
          )
        }

        await useProfileStore.getState().fetchProfile()
        const { optInState } = useProfileStore.getState()
        console.log('[bootstrap] spain: optInState:', optInState)
        if (optInState === 'error') {
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/error',
            errorData: { action: 'fetchProfile' },
          })
          return
        }
        if (optInState === 'missing') {
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/first-time-visit',
          })
          return
        }

        console.log('[bootstrap] spain: done → /home')
        set({ isInitialized: true, isLoading: false, bootstrapRoute: '/home' })
        return
      }

      // Romania: users arrive from the native app with a loyalty card userId in the URL.
      // That userId is exchanged for an MNGS session token.
      if (API_CONFIG.tenantCode === 'carrefourromania') {
        console.log('[bootstrap] romania flow')

        // Dev login sets a cookie token directly — skip Romania flow if already present
        if (getAccessToken()) {
          console.log('[bootstrap] romania: existing token, skipping auth flow')
        } else {
          const launchParameters = getRomaniaLaunchParameters()
          if (!launchParameters) {
            console.log('[bootstrap] romania: missing userId → /error')
            set({
              isInitialized: true,
              isLoading: false,
              bootstrapRoute: '/error',
              errorData: {
                code: 'missing_params',
                detail: 'Missing URL param: userId',
              },
            })
            return
          }

          try {
            console.log('[bootstrap] romania: authenticating')
            await useAuthStore
              .getState()
              .initializeRomania(launchParameters.userId)
          } catch (error) {
            const status = error instanceof ApiError ? error.status : undefined
            console.log('[bootstrap] romania: auth error → /error', status)
            set({
              isInitialized: true,
              isLoading: false,
              bootstrapRoute: '/error',
              errorData: { status, code: 'auth_failed' },
            })
            return
          }
        }

        try {
          await useBrandsStore.getState().fetchAvailableGamesForBrands()
        } catch {
          console.log(
            '[bootstrap] romania: available games fetch failed (non-blocking)'
          )
        }

        await useProfileStore.getState().fetchProfile()
        const { optInState } = useProfileStore.getState()
        console.log('[bootstrap] romania: optInState:', optInState)
        if (optInState === 'error') {
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/error',
            errorData: { action: 'fetchProfile' },
          })
          return
        }
        if (optInState === 'missing') {
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/first-time-visit',
          })
          return
        }

        console.log('[bootstrap] romania: done → /home')
        set({ isInitialized: true, isLoading: false, bootstrapRoute: '/home' })
        return
      }

      // Brazil: users arrive from a WebView that sets a Firebase JWT in the
      // ext-token-id cookie. That token is exchanged for an MNGS session token.
      if (API_CONFIG.tenantCode === 'carrefourbrazil') {
        console.log('[bootstrap] brazil flow')

        // Dev login sets a cookie token directly — skip Brazil flow if already present
        if (getAccessToken()) {
          console.log('[bootstrap] brazil: existing token, skipping auth flow')
        } else {
          const launchParameters = getBrazilLaunchParameters()
          if (!launchParameters) {
            console.log('[bootstrap] brazil: missing firebase token → /error')
            set({
              isInitialized: true,
              isLoading: false,
              bootstrapRoute: '/error',
              errorData: {
                code: 'missing_params',
                detail: 'Missing ext-token-id cookie',
              },
            })
            return
          }

          try {
            console.log('[bootstrap] brazil: authenticating')
            await useAuthStore
              .getState()
              .initializeBrazil(launchParameters.firebaseToken)
          } catch (error) {
            const status = error instanceof ApiError ? error.status : undefined
            console.log('[bootstrap] brazil: auth error → /error', status)
            set({
              isInitialized: true,
              isLoading: false,
              bootstrapRoute: '/error',
              errorData: { status, code: 'auth_failed' },
            })
            return
          }
        }

        try {
          await useBrandsStore.getState().fetchAvailableGamesForBrands()
        } catch {
          console.log(
            '[bootstrap] brazil: available games fetch failed (non-blocking)'
          )
        }

        await useProfileStore.getState().fetchProfile()
        const { optInState } = useProfileStore.getState()
        console.log('[bootstrap] brazil: optInState:', optInState)
        if (optInState === 'error') {
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/error',
            errorData: { action: 'fetchProfile' },
          })
          return
        }
        if (optInState === 'missing') {
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/first-time-visit',
          })
          return
        }

        console.log('[bootstrap] brazil: done → /home')
        set({ isInitialized: true, isLoading: false, bootstrapRoute: '/home' })
        return
      }

      // France: users arrive from a WebView that injects an STS token via cookie.
      // That token is exchanged for an MNGS session token via gravity auth, which also
      // returns a consent type used to determine if /marketing-consents is required.
      if (API_CONFIG.tenantCode === 'carrefour') {
        console.log('[bootstrap] france flow')

        try {
          await useAuthStore.getState().initialize()
        } catch {
          console.log('[bootstrap] france: auth failed → /error')
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/error',
            errorData: { code: 'auth_failed' },
          })
          return
        }
        const { isAuthenticated } = useAuthStore.getState()
        console.log('[bootstrap] france: isAuthenticated:', isAuthenticated)

        if (!isAuthenticated) {
          console.log('[bootstrap] france: not authenticated → /error')
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/error',
            errorData: {
              code: 'missing_token',
              detail: "No 'STS-TOKEN' cookie or 'accessToken' cookie found",
            },
          })
          return
        }

        try {
          await useBrandsStore.getState().fetchAvailableGamesForBrands()
        } catch {
          console.log(
            '[bootstrap] france: available games fetch failed (non-blocking)'
          )
        }

        await useProfileStore.getState().fetchProfile()
        const { optInState } = useProfileStore.getState()
        console.log('[bootstrap] france: optInState:', optInState)
        if (optInState === 'error') {
          console.log('[bootstrap] france: opt-in error → /error')
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/error',
            errorData: { action: 'fetchProfile' },
          })
          return
        }
        if (optInState === 'missing') {
          console.log('[bootstrap] france: opt-in missing → /first-time-visit')
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/first-time-visit',
          })
          return
        }

        // Consent type comes from gravity auth response
        const { gravityData } = useAuthStore.getState()
        const consentType = gravityData?.consent ?? null
        console.log('[bootstrap] france: consentType:', consentType)
        if (consentType && consentType !== MARKETING_CONSENT.NO_ALERT) {
          console.log(
            '[bootstrap] france: consent required → /marketing-consents'
          )
          set({
            isInitialized: true,
            isLoading: false,
            bootstrapRoute: '/marketing-consents',
          })
          return
        }

        console.log('[bootstrap] france: done → /home')
        set({ isInitialized: true, isLoading: false, bootstrapRoute: '/home' })
        return
      }

      // Unknown tenant — should never happen in production
      console.error(
        '[bootstrap] unknown tenant:',
        API_CONFIG.tenantCode,
        '→ /error'
      )
      set({
        isInitialized: true,
        isLoading: false,
        bootstrapRoute: '/error',
        errorData: { code: 'unknown_tenant' },
      })
    } catch (error) {
      console.error('[bootstrap] caught error:', error)
      set({ isInitialized: true, isLoading: false, bootstrapRoute: '/home' })
    }
  },

  setInitialized: value => set({ isInitialized: value }),
  setLoading: value => set({ isLoading: value }),
  setError: error => set({ error }),
  setErrorData: errorData => set({ errorData }),
  reset: () =>
    set({
      isInitialized: false,
      isLoading: false,
      error: null,
      errorData: null,
      bootstrapRoute: null,
    }),
}))
